import { useState, useEffect } from 'react';
import {
  getAffiliations,
  updateAffiliations,
  getAffiliationsCatalog,
  Affiliation,
  CatalogAffiliation,
} from '../api/client';
import ImageUploader from '../components/ImageUploader';
import { useSportConfig } from '../hooks/useSportConfig';
import { normalizeUrlInput } from '../utils/url';
import './Affiliations.css';

const genId = () => `aff-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

function Affiliations() {
  const sportConfig = useSportConfig();
  const [items, setItems] = useState<Affiliation[]>([]);
  const [catalog, setCatalog] = useState<CatalogAffiliation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [affs, cat] = await Promise.all([getAffiliations(), getAffiliationsCatalog(sportConfig.id)]);
        setItems(affs);
        setCatalog(cat);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load affiliations');
      } finally {
        setIsLoading(false);
      }
    })();
  }, [sportConfig.id]);

  const update = (id: string, field: keyof Affiliation, value: string) =>
    setItems(prev => prev.map(a => (a.id === id ? { ...a, [field]: value } : a)));

  const remove = (id: string) => setItems(prev => prev.filter(a => a.id !== id));

  const addFromCatalog = (c: CatalogAffiliation) => {
    if (items.some(a => a.name.trim().toLowerCase() === c.name.trim().toLowerCase())) return;
    setItems(prev => [...prev, { id: genId(), name: c.name, image: c.image, url: c.url }]);
  };

  const addBlank = () =>
    setItems(prev => [...prev, { id: genId(), name: '', image: '', url: '' }]);

  const handleSave = async () => {
    setError('');
    setSuccessMessage('');
    setIsSaving(true);
    try {
      const saved = await updateAffiliations(items.filter(a => a.name.trim()));
      setItems(saved);
      setSuccessMessage('Affiliations saved');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save affiliations');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) return <div className="loading">Loading affiliations...</div>;

  const usedNames = new Set(items.map(a => a.name.trim().toLowerCase()));

  return (
    <div className="affiliations-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Affiliations</h1>
          <p className="page-subtitle">
            Leagues, county FA, The FA, shown as a logo strip on your home page
          </p>
        </div>
        <button onClick={handleSave} className="btn btn-primary" disabled={isSaving || imageUploading}>
          {isSaving ? 'Saving...' : imageUploading ? 'Uploading…' : 'Save'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {successMessage && <div className="success-message">{successMessage}</div>}

      <div className="aff-quickadd">
        <span className="aff-quickadd-label">Quick add:</span>
        {catalog.map(c => (
          <button
            key={c.id}
            type="button"
            className="aff-chip"
            disabled={usedNames.has(c.name.trim().toLowerCase())}
            onClick={() => addFromCatalog(c)}
          >
            {c.image && <img src={c.image} alt="" className="aff-chip-logo" />}
            {c.name}
          </button>
        ))}
        <button type="button" className="aff-chip aff-chip-own" onClick={addBlank}>
          + Add your own
        </button>
      </div>

      {items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏅</div>
          <p className="empty-state-text">
            No affiliations yet, quick-add The FA / England Football above, or add your own
            league and county FA.
          </p>
        </div>
      ) : (
        <div className="aff-list">
          {items.map(a => (
            <div key={a.id} className="aff-row">
              <div className="aff-row-logo">
                <ImageUploader
                  onUpload={url => update(a.id, 'image', url)}
                  currentImage={a.image}
                  onUploadingChange={setImageUploading}
                  label="Logo"
                />
              </div>
              <div className="aff-row-fields">
                <input
                  type="text"
                  className="form-input"
                  placeholder="Name (e.g. Cambridgeshire FA)"
                  value={a.name}
                  onChange={e => update(a.id, 'name', e.target.value)}
                />
                <input
                  type="text"
                  className="form-input"
                  placeholder="Website, e.g. www.cambridgeshirefa.com"
                  value={a.url || ''}
                  onChange={e => update(a.id, 'url', e.target.value)}
                  onBlur={e => update(a.id, 'url', normalizeUrlInput(e.target.value))}
                />
              </div>
              <button type="button" className="btn btn-danger btn-small" onClick={() => remove(a.id)}>
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Affiliations;
